import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

interface ServiceWorkerState {
  isSupported: boolean
  isInstalled: boolean
  isActive: boolean
  isUpdateAvailable: boolean
  isOffline: boolean
}

interface ServiceWorkerActions {
  register: () => Promise<void>
  unregister: () => Promise<void>
  update: () => Promise<void>
  skipWaiting: () => Promise<void>
  clearCache: () => Promise<void>
}

export function useServiceWorker(): [ServiceWorkerState, ServiceWorkerActions] {
  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: 'serviceWorker' in navigator,
    isInstalled: false,
    isActive: false,
    isUpdateAvailable: false,
    isOffline: !navigator.onLine,
  })

  // Verificar status do Service Worker
  const checkServiceWorker = useCallback(async () => {
    if (!state.isSupported) return

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      
      if (registration) {
        setState(prev => ({
          ...prev,
          isInstalled: true,
          isActive: !!registration.active,
          isUpdateAvailable: !!registration.waiting,
        }))

        // Listener para atualizações
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setState(prev => ({ ...prev, isUpdateAvailable: true }))
              }
            })
          }
        })
      }
    } catch (error) {
      console.error('❌ Erro ao verificar Service Worker:', error)
    }
  }, [state.isSupported])

  // Registrar Service Worker
  const register = useCallback(async () => {
    if (!state.isSupported) {
      throw new Error('Service Worker não é suportado')
    }

    try {
      // Evitar registro duplicado: reutilizar se já existir
      const existing = await navigator.serviceWorker.getRegistration()
      const registration = existing ?? await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      })

      console.log(existing ? 'ℹ️ Service Worker já estava registrado' : '✅ Service Worker registrado:', registration)

      setState(prev => ({
        ...prev,
        isInstalled: true,
        isActive: !!registration.active,
      }))

      // Listener para mudanças de estado
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setState(prev => ({ ...prev, isUpdateAvailable: true }))
            }
          })
        }
      })

      // Listener para controller change — só actualiza o estado; não recarrega a página
      // (o reload automático interrompia auth mid-load quando o SW actualizava)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setState(prev => ({ ...prev, isActive: true, isUpdateAvailable: false }))
      })

    } catch (error) {
      console.error('❌ Erro ao registrar Service Worker:', error)
      throw error
    }
  }, [state.isSupported])

  // Desregistrar Service Worker
  const unregister = useCallback(async () => {
    if (!state.isSupported) return

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        await registration.unregister()
        setState(prev => ({
          ...prev,
          isInstalled: false,
          isActive: false,
          isUpdateAvailable: false,
        }))
        console.log('✅ Service Worker desregistrado')
      }
    } catch (error) {
      console.error('❌ Erro ao desregistrar Service Worker:', error)
      throw error
    }
  }, [state.isSupported])

  // Atualizar Service Worker
  const update = useCallback(async () => {
    if (!state.isSupported) return

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        await registration.update()
        console.log('🔄 Service Worker atualizado')
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar Service Worker:', error)
      throw error
    }
  }, [state.isSupported])

  // Pular espera (ativar nova versão imediatamente)
  const skipWaiting = useCallback(async () => {
    if (!state.isSupported) return

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
        console.log('⏭️ Service Worker skip waiting')
      }
    } catch (error) {
      console.error('❌ Erro ao skip waiting:', error)
      throw error
    }
  }, [state.isSupported])

  // Limpar cache
  const clearCache = useCallback(async () => {
    if (!state.isSupported) return

    try {
      const cacheNames = await caches.keys()
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      )
      console.log('🗑️ Cache limpo')
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error)
      throw error
    }
  }, [state.isSupported])

  // Monitorar status online/offline
  useEffect(() => {
    const handleOnline = () => setState(prev => ({ ...prev, isOffline: false }))
    const handleOffline = () => setState(prev => ({ ...prev, isOffline: true }))

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Verificar Service Worker na inicialização
  useEffect(() => {
    checkServiceWorker()
  }, [checkServiceWorker])

  const actions: ServiceWorkerActions = {
    register,
    unregister,
    update,
    skipWaiting,
    clearCache,
  }

  return [state, actions]
}

// Hook para sincronização offline
export function useOfflineSync() {
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSync, setLastSync] = useState<Date | null>(null)

  const syncOfflineData = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('sync' in window.ServiceWorkerRegistration.prototype)) {
      console.warn('⚠️ Background Sync não suportado')
      return
    }

    try {
      setIsSyncing(true)
      
      const registration = await navigator.serviceWorker.ready
      await (registration as any).sync.register('background-sync')
      
      setLastSync(new Date())
      console.log('🔄 Sincronização offline agendada')
    } catch (error) {
      console.error('❌ Erro ao agendar sincronização:', error)
    } finally {
      setIsSyncing(false)
    }
  }, [])

  return {
    isSyncing,
    lastSync,
    syncOfflineData,
  }
}

// Converter base64url → ArrayBuffer (para applicationServerKey)
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes.buffer
}

// Hook para notificações push — VAPID + Supabase
export function usePushNotifications(userId?: string) {
  const [isSupported, setIsSupported] = useState(false)
  const [permission, setPermission]   = useState<NotificationPermission>('default')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading]     = useState(false)

  useEffect(() => {
    setIsSupported('serviceWorker' in navigator && 'PushManager' in window)
    if (typeof Notification !== 'undefined') setPermission(Notification.permission)
  }, [])

  // Verificar subscrição existente quando suportado
  useEffect(() => {
    if (!isSupported) return
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setIsSubscribed(!!sub))
    ).catch(() => {})
  }, [isSupported])

  // Auto-reactivar se permissão já concedida e userId disponível
  useEffect(() => {
    if (!userId || !isSupported || isSubscribed || isLoading) return
    if (Notification.permission !== 'granted') return
    const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined
    if (!vapid) return
    enablePush(userId, vapid).catch(() => {})
  }, [userId])

  const enablePush = useCallback(async (uid: string, vapidKey: string) => {
    setIsLoading(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const old = await reg.pushManager.getSubscription()
      if (old) await old.unsubscribe()

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      const subJson  = sub.toJSON()
      const endpoint = subJson.endpoint!
      const keys     = subJson.keys as Record<string, string>

      const { error } = await supabase.from('push_subscriptions').upsert(
        {
          user_id:    uid,
          endpoint,
          p256dh:     keys['p256dh']!,
          auth:       keys['auth']!,
          user_agent: navigator.userAgent.slice(0, 255),
        },
        { onConflict: 'user_id,endpoint' }
      )
      if (error) throw new Error(error.message)

      setIsSubscribed(true)
      console.log('✅ Push subscribed and saved (clientes)')
    } catch (err) {
      console.error('❌ Push subscribe error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const requestPermission = useCallback(async () => {
    if (!isSupported) throw new Error('Push notifications não suportadas')
    const result = await Notification.requestPermission()
    setPermission(result)
    return result
  }, [isSupported])

  const subscribe = useCallback(async (vapidPublicKey: string) => {
    if (!isSupported || permission !== 'granted') throw new Error('Permissão não concedida')
    if (userId) await enablePush(userId, vapidPublicKey)
  }, [isSupported, permission, userId, enablePush])

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        const endpoint = sub.endpoint
        await sub.unsubscribe()
        if (userId) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', userId)
            .eq('endpoint', endpoint)
        }
        setIsSubscribed(false)
        console.log('✅ Push unsubscribed')
      }
    } catch (error) {
      console.error('❌ Erro ao remover inscrição:', error)
      throw error
    }
  }, [userId])

  /** Chamar no logout — best-effort */
  const onLogout = useCallback(async () => {
    try { await unsubscribe() } catch { /* silent */ }
  }, [unsubscribe])

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    requestPermission,
    subscribe,
    unsubscribe,
    onLogout,
  }
}

// ── Helper: enviar push para outro utilizador via Edge Function ────────────────

export async function sendPushToUser(
  recipientId: string,
  title: string,
  message: string,
  url = '/'
): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('send-push', {
      body: { userId: recipientId, title, message, url },
    })
    if (error) console.warn('[push] sendPushToUser error:', error)
  } catch (err) {
    // best-effort: não bloquear o fluxo principal
    console.warn('[push] sendPushToUser exception:', err)
  }
}